import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import queries from 'api/queries/ProductQueries';
import { errorToast, successToast } from 'components/common/Toast/Toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const useProductTypesLogic = (modifiers) => {
  const [selectedEdit, setSelectedEdit] = useState(null);
  const [prio2Edit, setPrio2Edit] = useState(null);
  const [prio2Modifiers, setPrio2Modifiers] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isDialog, setIsDialog] = useState(false);
  const [datagridData, setDatagridData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(-1);
  const shopId = useSelector((state) => state.nonPersistedReducers?.shopSliceReducer?.shopId);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  useEffect(() => {
    if (selectedEdit) {
      createDatagridData(selectedEdit);
    }
  }, [modifiers?.data]);

  const createModifier = useMutation({
    mutationFn: (modifier) => queries.postModifier({ shopId: shopId, modifier: modifier }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['currentModifiers'] });
      setSelectedMethod(-1);
      successToast('Sikeres létrehozás!');
      setSelectedEdit(data?.data);
    },
    onError: () => {
      errorToast('Sikertelen létrehozás!');
    }
  });
  const createPrio2Modifier = useMutation({
    mutationFn: (modifier) => queries.postModifier({ shopId: shopId, modifier: modifier }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['currentModifiers'] });
      successToast('Sikeres létrehozás!');
    },
    onError: () => {
      errorToast('Sikertelen létrehozás!');
    }
  });
  const updateModifier = useMutation({
    mutationFn: (modifier) => queries.putModifier({ shopId: shopId, modifier: modifier }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['currentModifiers'] });
      successToast('Sikeres szerkesztés!');
      setSelectedEdit(data?.data);
    },
    onError: () => {
      errorToast('Sikertelen szerkesztés!');
    }
  });
  const updatePrio2Modifier = useMutation({
    mutationFn: (modifier) => queries.putModifier({ shopId: shopId, modifier: modifier }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentModifiers'] });
      successToast('Sikeres szerkesztés!');
    },
    onError: () => {
      errorToast('Sikertelen szerkesztés!');
    }
  });
  const deleteModifier = useMutation({
    mutationFn: (variables) => queries.deleteModifier({ shopId: shopId, modifierId: variables?.modifierId }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['currentModifiers'] });
      variables?.setOpen(false);
      successToast('Sikeres törlés!');
    },
    onError: () => {
      errorToast('Sikertelen törlés!');
    }
  });
  const addPredefinedModifier = useMutation({
    mutationFn: (variables) =>
      queries.addPredefinedModifier({ shopId: shopId, deliveryType: selectedMethod.deliveryType }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['currentModifiers'] });
      resetForm();
      variables?.setOpen(false);
      successToast('Sikeres hozzáadás!');
    },
    onError: () => {
      errorToast('Sikertelen hozzáadás!');
      resetForm();
    }
  });

  const handleSubmit = (values) => {
    if (selectedEdit === null) {
      createModifier.mutate(values);
    } else {
      updateModifier.mutate(values);
    }
  };

  const createDatagridData = (data) => {
    setDatagridData([]);
    if (!data) return;

    const datas = modifiers?.data.filter((el) => {
      return el.priority === 2 && data.compatibleGroup === el.compatibleGroup;
    });
    setDatagridData(
      datas?.map((el) => ({
        id: el.id,
        name:
          t(`templates.productTypes.${el.name}`) != `templates.productTypes.${el.name}`
            ? t(`templates.productTypes.${el.name}`)
            : el.name,
        description:
          t(`templates.productTypes.${el.description}`) != `templates.productTypes.${el.description}`
            ? t(`templates.productTypes.${el.description}`)
            : el.description,
        category:
          t(`templates.productTypes.${el.category}`) != `templates.productTypes.${el.category}`
            ? t(`templates.productTypes.${el.category}`)
            : el.category
      }))
    );
    setPrio2Modifiers(datas);
  };

  const handleProductTypeSelect = (productType) => {
    setSelectedEdit(productType);
    createDatagridData(productType);
    setIsEdit(true);
  };

  const handleAddNewDialog = () => {
    setSelectedEdit(null);
    setIsDialog(true);
  };

  const handleCloseNewDialog = () => {
    setIsDialog(false);
    setSelectedMethod(-1);
  };

  const changeMethod = (answer) => {
    setSelectedMethod(answer);
  };

  const handleAddNew = () => {
    setSelectedEdit(null);
    setIsDialog(false);
    if (!selectedMethod.deliveryType) {
      setIsEdit(true);
    } else {
      addPredefinedModifier.mutate();
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogSubmit = async (values) => {
    if (prio2Edit) {
      updatePrio2Modifier.mutate(values);
      handleDialogClose();
      setPrio2Edit(null);
    } else {
      createPrio2Modifier.mutate(values);
      handleDialogClose();
    }
  };

  const resetForm = () => {
    setSelectedEdit(null);
    setSelectedMethod(-1);
    setDatagridData([]);
    setIsEdit(false);
    setIsDialog(false);
  };

  return {
    selectedEdit,
    setSelectedEdit,
    prio2Edit,
    setPrio2Edit,
    isEdit,
    setIsEdit,
    datagridData,
    setDatagridData,
    dialogOpen,
    setDialogOpen,
    createModifier,
    createPrio2Modifier,
    updateModifier,
    updatePrio2Modifier,
    deleteModifier,
    handleProductTypeSelect,
    handleAddNew,
    handleDialogClose,
    handleDialogSubmit,
    resetForm,
    handleSubmit,
    prio2Modifiers,
    handleAddNewDialog,
    handleCloseNewDialog,
    isDialog,
    selectedMethod,
    changeMethod,
    shopId,
    navigate
  };
};
